import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { useState } from 'react';

import { Tooltip } from '@poseidon/components/Input2';

import { SequenceOwnerBadge } from '~/components/Sequence/SequenceOwnerBadge';
import { SequenceOwnerPicker } from '~/components/Sequence/SequenceOwnerPicker';

// Stable middleware identity (re-anchored every pan/zoom frame): `flip` drops the list above when there's
// no room below, `shift` keeps it on-screen near an edge.
const MIDDLEWARE = [offset(8), flip({ padding: 8 }), shift({ padding: 8 })];

const TOOLTIP =
  'Everyone in this sequence is worked on behalf of this person: touches send from their LinkedIn account, inside their working hours and daily limits, and their call/reminder to-dos land on their list.';

interface Props {
  teamId: string;
  ownerUserId: string;
  ownerName: string | null;
  ownerProfilePicture: string | null;
  /** Reassign the sequence. Omit → a static badge, not a picker. */
  onChangeOwner?: (userId: string) => void;
}

/**
 * The sequence's OWNER — the top of the spine, above the automatic research steps: a circular avatar with
 * the person's name beside it. Every enrollment runs on their behalf, so this is the one node that isn't a
 * stage in the journey but a property of the whole sequence.
 *
 * Clicking opens the teammate list in a **portaled** popover so it escapes the canvas's `overflow-hidden`,
 * isn't painted under an adjacent node, and renders at natural size whatever the zoom. Open/close is
 * Floating UI's `useClick` + `useDismiss` rather than a hand-rolled blur-out: the list has no in-flight
 * edit to commit, and blur-out only dismisses while something INSIDE the popover holds focus — which is
 * never true for a small team, where the search field isn't rendered at all.
 */
export function SequenceOwnerNode({ teamId, ownerUserId, ownerName, ownerProfilePicture, onChangeOwner }: Props) {
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-start',
    strategy: 'fixed',
    // Track the avatar as the canvas is panned/zoomed by CSS transform (scroll/resize observers miss that).
    whileElementsMounted: (reference, floating, update) => autoUpdate(reference, floating, update, { animationFrame: true }),
    middleware: MIDDLEWARE,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context),
    useRole(context, { role: 'menu' }),
  ]);

  const editable = onChangeOwner != null;

  const badge = (
    <SequenceOwnerBadge
      ownerName={ownerName}
      ownerProfilePicture={ownerProfilePicture}
      editable={editable}
      active={open}
    />
  );

  return (
    <>
      {/* The tooltip is suppressed while the list is open so the two overlays don't stack. */}
      <Tooltip as="div" tooltip={open ? undefined : TOOLTIP} tooltipMaxWidth={280}>
        {/* DELIBERATE raw <button> rather than Input2's Button (repo CLAUDE.md's default). Button wraps its
            children in an `overflow-hidden text-ellipsis` slot sized for a label, which clips this node's
            name column to zero width — it contributes nothing here but that clip, since the avatar circle,
            ring and two-line caption are all bespoke. The sibling graph nodes on this same canvas
            (SequencePreStepNode, SequencePillPopover) are raw buttons for the same reason. */}
        {!editable && <div className="group/owner cursor-default">{badge}</div>}
        {editable && (
          <button
            ref={refs.setReference}
            type="button"
            aria-label={`Change who this sequence runs as (currently ${ownerName ?? 'nobody'})`}
            className="group/owner cursor-pointer rounded-full"
            {...getReferenceProps()}>
            {badge}
          </button>
        )}
      </Tooltip>
      {editable && open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            data-no-pan
            data-sequence-owner-picker
            className="z-50 rounded-lg border border-lynch-200 bg-white p-1.5 shadow-md dark:border-slate-700 dark:bg-slate-900"
            {...getFloatingProps()}>
            <SequenceOwnerPicker
              teamId={teamId}
              ownerUserId={ownerUserId}
              onPick={(userId) => {
                setOpen(false);
                if (userId !== ownerUserId) onChangeOwner(userId);
              }}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
